import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { isEmpty, mapValues } from 'mojito/utils';
import { createRef, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMidnightEffect } from 'modules/filters-bar/hooks';
import FiltersBarSkeleton from 'modules/filters-bar/skeleton/index.js';
import FilterItem from 'modules/filters-bar/item/index.js';

const {
    DateTimeUtils: { addDays, getCustomFormattedDate, getDateWithGmtOffset, getSignedGmtOffset },
    DateTimeTypes: { DATE_FORMAT_OPTIONS },
} = MojitoCore.Base;
const { ButtonScrollPane, LoaderSuspense, FlexPane, Image } = MojitoPresentation.Components;
const { selectTimeZoneGmt } = MojitoServices.UserSettings.selectors;
const { selectLanguage } = MojitoCore.Services.SystemSettings.selectors;
const SportUtils = MojitoServices.SportsContent.Sports.utils;

const SUPPORTED_DAY_FORMAT_OPTIONS = Object.values(DATE_FORMAT_OPTIONS);
const DAY_FILTER = 'day';

const getKey = (filter = {}) => filter.span || filter.dayOffset || filter.type;
const isDayFilter = filterType => filterType === DAY_FILTER;
const convertDayOffsetToDate = (dayOffset, timeZone) =>
    addDays(getDateWithGmtOffset(getSignedGmtOffset(timeZone)), dayOffset);
const resolveDayFilterLabel = (dayOffset, dayFilterNamesDeps, sportId, stringResolver) => {
    const { defaultNamingSchemaForDayFilters, selectedTimeZone, selectedLocale } =
        dayFilterNamesDeps;
    const dateWithOffset = convertDayOffsetToDate(dayOffset, selectedTimeZone);
    const DAY_FILTERS_PREFIX = `$FILTERS_BAR.${SportUtils.isRacingSport(sportId) ? 'RACING_BASED_SPORTS' : 'MATCH_BASED_SPORTS'}.DAY.`;
    const schemaOverride =
        stringResolver.resolveString(`${DAY_FILTERS_PREFIX}${sportId}.${dayOffset}`, true) ||
        stringResolver.resolveString(`${DAY_FILTERS_PREFIX}${dayOffset}`, true);
    const isDynamic =
        schemaOverride &&
        schemaOverride.split(' ').some(s => SUPPORTED_DAY_FORMAT_OPTIONS.includes(s));

    let namingSchema = defaultNamingSchemaForDayFilters;

    if (isDynamic) {
        namingSchema = schemaOverride;
    }

    const isFixed = schemaOverride && !isDynamic;

    return isFixed
        ? schemaOverride
        : getCustomFormattedDate(dateWithOffset, namingSchema, selectedLocale);
};

const resolveBaseFilterLabel = (key, sportId, stringResolver) => {
    const [, numberFromKey, valueFromKey] = key.split(/(\d+)/);
    const FILTERS_PREFIX = `$FILTERS_BAR.${SportUtils.isRacingSport(sportId) ? 'RACING_BASED_SPORTS' : 'MATCH_BASED_SPORTS'}.BASE.`;

    return (
        stringResolver.resolveString(`${FILTERS_PREFIX}${sportId}.${key.toUpperCase()}`, true) ||
        stringResolver.resolveString(`${FILTERS_PREFIX}${key.toUpperCase()}`, true) ||
        stringResolver.resolveAndFormatString(
            `${FILTERS_PREFIX}${valueFromKey.toUpperCase()}`,
            numberFromKey
        )
    );
};

const convertToFilterItems = (
    filters,
    sportId,
    stringResolver,
    mojitoTools,
    { dayFilterNamesDeps }
) => {
    const resolveTranslation = filter => {
        if (isDayFilter(filter.type)) {
            return resolveDayFilterLabel(
                filter.dayOffset,
                dayFilterNamesDeps,
                sportId,
                stringResolver
            );
        }

        return resolveBaseFilterLabel(getKey(filter), sportId, stringResolver);
    };

    return filters.map(filter => ({
        key: `${getKey(filter)}`,
        label: resolveTranslation(filter),
        group: filter.group,
        iconRenderer: isSelected => iconRenderer({ key: getKey(filter), isSelected, mojitoTools }),
    }));
};

const FiltersBar = ({
    sportId,
    filters,
    selectedFilterKey,
    onFilterClicked,
    getItemHrefLink,
    mojitoTools,
}) => {
    const { config, stringResolver } = mojitoTools;
    const [midnightReRenderTrigger, setMidnightReRenderTrigger] = useState(0);
    const timeZone = useSelector(state => selectTimeZoneGmt(state));
    const locale = useSelector(state => selectLanguage(state));

    useMidnightEffect(getDateWithGmtOffset(getSignedGmtOffset(timeZone)), () => {
        setMidnightReRenderTrigger(prev => prev + 1);
    });

    const filterItems = useMemo(
        () =>
            filters
                ? convertToFilterItems(filters, sportId, stringResolver, mojitoTools, {
                      dayFilterNamesDeps: {
                          defaultNamingSchemaForDayFilters: config.defaultNamingSchemaForDayFilters,
                          selectedTimeZone: timeZone,
                          selectedLocale: locale,
                      },
                  })
                : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters, locale, sportId, timeZone, stringResolver, mojitoTools, midnightReRenderTrigger]
    );

    const filtersBarSkeleton = <FiltersBarSkeleton config={config.topMenuSkeleton} />;

    const itemRefs = filterItems.reduce((refs, item) => {
        refs[item.key] = createRef();
        return refs;
    }, {});

    return (
        <>
            <LoaderSuspense
                config={config.skeletonLoader}
                loader={filtersBarSkeleton}
                isContentPending={isEmpty(filters)}
            >
                <ButtonScrollPane
                    config={config.buttonScrollPane}
                    refs={itemRefs}
                    selectedItemKey={selectedFilterKey}
                >
                    {filterItems.map(item => {
                        return (
                            <FilterItem
                                key={item.key}
                                item={item}
                                config={config.filterItem}
                                refs={itemRefs}
                                isSelected={item.key === selectedFilterKey}
                                getItemHrefLink={getItemHrefLink}
                                onFilterClicked={onFilterClicked}
                            />
                        );
                    })}
                </ButtonScrollPane>
            </LoaderSuspense>
        </>
    );
};

const iconRenderer = ({ key, isSelected, mojitoTools: { config, style } }) =>
    config.icons?.[key] && (
        <FlexPane config={config.iconContainer}>
            <Image config={isSelected ? style.selectedIcons[key] : config.icons[key]} />
        </FlexPane>
    );

FiltersBar.getStyle = function (config) {
    const selectedIcons = mapValues(config.icons, icon => ({
        ...icon,
        style: {
            ...icon.style,
            fill: config.selectedIconColor,
        },
    }));

    return {
        selectedIcons,
    };
};

export default FiltersBar;
